import { Account, Flag, Organization, Service, Variant } from '@acies/domain';
import { FeatureFlagsJSONSchema } from '@acies/schema';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from '@environments/environment';
import { handleDomainError } from '@shared/errors/domain-errors';
import { SessionStorageService } from '@shared/services/storage.service';
import { UserService } from '@shared/services/user.service';
import { catchError, retry } from 'rxjs';
import { parse, transformFeatureFlags } from './adapters';

export class AccountFlagsService {
	get #URL(): string {
		return `${this.#baseURL}/${this.organization.id}/accounts/${this.account.id}/featureflags/variants`;
	}

	#account: Account | null;
	#baseURL: string;
	#headers: HttpHeaders;
	#httpClient: HttpClient;
	#organization: Organization | null;
	#sessionStorageService: SessionStorageService;
	#userService: UserService;
	public constructor(httpClient: HttpClient, sessionStorageService: SessionStorageService, userService: UserService) {
		this.#account = null;
		this.#organization = null;
		this.#baseURL = `${environment.awsEnvUri}/iam/organizations`;
		this.#httpClient = httpClient;
		this.#sessionStorageService = sessionStorageService;
		this.#userService = userService;
	}

	public loadVariants(refresh = false) {
		// if refresh is true, the account has changed,
		// so we nullify the references so they get updated,
		// when calling the getter methods
		if (refresh) {
			this.#account = null;
			this.#organization = null;
		}
		return this.#httpClient
			.get<Service[]>(`${this.#URL}`, {
				headers: this.headers,
			})
			.pipe(retry(1), parse(FeatureFlagsJSONSchema), transformFeatureFlags(this.account), catchError(handleDomainError));
	}

	public updateFlag(flag: Flag, variant: Variant) {
		const body = {
			service: flag.service,
			accountId: this.account.id,
			enabled: variant.enabled,
		};
		return this.#httpClient.put(`${this.#URL}/${flag.name}`, body, { headers: this.headers }).pipe(catchError(handleDomainError));
	}

	/** Constructs the single Account instance we need when calling the API with the id, and when constructing the Variant that demands an Account instance reference. */
	private get account(): Account {
		if (!this.#account) {
			const { id, name } = this.#userService.account;
			const organization = this.organization;
			this.#account = new Account({ id, name, organization });
		}
		return this.#account;
	}

	private get headers(): HttpHeaders {
		if (!this.#headers) {
			this.#headers = new HttpHeaders({
				Authorization: `Bearer ${this.#sessionStorageService.get('User.Authorization')}`,
			});
		}
		return this.#headers;
	}

	/** Constructs a Organization instance from the UserService but don't add the Account to the accounts Array, since that would cause a stack overflow when two instances references eachother, so we set the accounts as an empty Array here and push the Account later. */
	private get organization(): Organization {
		if (!this.#organization) {
			const { id, name } = this.#userService.account.organization;
			this.#organization = new Organization({ accounts: [], id, name });
		}
		return this.#organization;
	}
}
